import { Tabela } from "shared/components/table/Tabela";
import { TableColumn } from "types/table/TableColumn";
import { useProcessosJudiciaisContext } from "context/gestaoCobranca/ProcessosJudiciaisContext";
import React, { useEffect, useState } from "react";
import { OpcoesSelecionaveisCheckBox } from "types/OpcoesSelecionaveisCheckBoxType";
import { RegistroContaReceberDTO } from "types/dto/RegistroContaReceberDTO";
import { useGetCobrancasVinculadasAoProcesso } from "hooks/processosJudiciais/useGetCobrancasVinculadasAoProcesso";
import { useLocation } from "react-router-dom";
import { RenderRowsCobrancasVinculadasAoProcesso } from "./RenderRowsCobrancasVinculadasAoProcesso";
import { queryClient } from "shared/util/queryClient";

const TabelaCobrancasVinculadasAoProcesso: React.FC = (props) => {
  const [isCheckBoxChecked, setIsCheckBoxChecked] = useState<boolean>(false);
  const [ordenacaoAscOuDesc, setOrdenacaoAscOuDesc] = useState<"ASC" | "DESC">("ASC");
  const [ordenarPorColuna, setOrdenarPorColuna] = useState<string>("");
  const [sortString, setSortString] = useState<string>("");
  const location = useLocation();
  const [processoJudicialId, setProcessoJudicialId] = useState<number | undefined>();

  const [paginaAtual, setPaginaAtual] = useState<number>(1);

  const { data: cobrancasPage, isLoading } = useGetCobrancasVinculadasAoProcesso({
    processoJudicialId: processoJudicialId,
    page: paginaAtual - 1,
    size: 10,
    sort: sortString,
  });
  const {
    idsCobrancasVinculadasAoProcessoSelecionadas: idsCobrancasSelecionadas,
    setIdsCobrancasVinculadasAoProcessoSelecionadas: setIdsCobrancasSelecionadas,
    opcaoCheckBoxSelecionarCobrancasVinculadasAoProcesso: valueCheckBox,
    setOpcaoCheckBoxSelecionarCobrancasVinculadasAoProcesso: setValueCheckBox,
    isVisualizacao,
    possuiPermissaoIniciarAcordo,
  } = useProcessosJudiciaisContext();

  useEffect(() => {
    setProcessoJudicialId(location.state.processoJudicialId);
    return () => {
      queryClient.removeQueries("cobrancas-vinculadas-ao-processo");
      queryClient.removeQueries("ids-cobrancas-vinculadas-ao-processo");
    };
  }, []);

  useEffect(() => {
    if (
      !(valueCheckBox === "TODAS_FILTRO") &&
      !isLoading &&
      cobrancasPage?.content &&
      cobrancasPage.content.length > 0
    ) {
      if (isTodosRegistrosDaPaginaSelecionados()) {
        setValueCheckBox("TODAS_PAGINA");
        setIsCheckBoxChecked(true);
      } else {
        setValueCheckBox("NENHUMA");
        setIsCheckBoxChecked(false);
      }
    }
  }, [cobrancasPage?.content, idsCobrancasSelecionadas]);

  const isTodosRegistrosDaPaginaSelecionados = () => {
    let idsCobrancasPagina = cobrancasPage?.content.map((cobranca: RegistroContaReceberDTO) => cobranca.id) || [];
    return idsCobrancasPagina.every((id: number) => idsCobrancasSelecionadas.includes(id));
  };

  const createColumnsInadimplencia = (): TableColumn[] => {
    return [
      {
        id: "contaReceber.mesCompetencia",
        label: "Competência",
        isOrdenavel: true,
      },
      {
        id: "contaReceber.nossoNumero",
        label: "Nosso número",
        isOrdenavel: true,
      },
      {
        id: "contaReceber.dataVencimento",
        label: "Vencimento",
        isOrdenavel: true,
      },
      {
        id: "total",
        label: "Valor (R$)",
        width: "130px",
      },
      {
        id: "acoes",
        label: "",
        width: "80px",
        textAlign: "start",
      },
    ];
  };

  const ordenarRegistros = (idColuna: string) => {
    const novaOrdenacao =
      !ordenacaoAscOuDesc || ordenarPorColuna !== idColuna || ordenacaoAscOuDesc === "DESC" ? "ASC" : "DESC";
    setOrdenacaoAscOuDesc(novaOrdenacao);
    setOrdenarPorColuna(idColuna);
    setSortString(`${idColuna},${novaOrdenacao}`);
  };

  const changeCheckBox = (opcao: OpcoesSelecionaveisCheckBox) => {
    if (opcao === "TODAS_FILTRO") {
      setIdsCobrancasSelecionadas([]);
      setIsCheckBoxChecked(true);
    } else if (opcao === "TODAS_PAGINA" && !(valueCheckBox === "TODAS_PAGINA")) {
      let cobrancasPagina = cobrancasPage?.content || [];
      let idsCobrancasPagina = cobrancasPagina.map((cobranca: RegistroContaReceberDTO) => cobranca.id);
      let novosIdsCobrancasSelecionadas = [...idsCobrancasSelecionadas, ...idsCobrancasPagina];
      setIdsCobrancasSelecionadas(novosIdsCobrancasSelecionadas);
      setIsCheckBoxChecked(true);
    } else if (opcao === "NENHUMA") {
      setIdsCobrancasSelecionadas([]);
      setIsCheckBoxChecked(false);
    }
    setValueCheckBox(opcao);
  };

  const onSelecionarCobranca = (cobranca: RegistroContaReceberDTO) => {
    let idsCobrancas = [...idsCobrancasSelecionadas, cobranca.id];

    setIdsCobrancasSelecionadas(idsCobrancas);
  };
  const onDesmarcarCobranca = (cobranca: RegistroContaReceberDTO) => {
    let idsCobrancas = idsCobrancasSelecionadas.filter((id) => id !== cobranca.id);

    setIdsCobrancasSelecionadas(idsCobrancas);
    setIsCheckBoxChecked(false);
  };

  const onChangePage = (novaPagina: number) => {
    if (novaPagina !== paginaAtual) {
      setPaginaAtual(novaPagina);
    }
  };

  return (
    <Tabela
      id="tabela-cobrancas-vinculadas-ao-processo"
      colunas={createColumnsInadimplencia()}
      ordernarPorColuna={ordenarPorColuna || ""}
      ordenacaoAscOuDesc={ordenacaoAscOuDesc}
      ordenarRegistrosFunction={(idColuna) => ordenarRegistros(idColuna)}
      onChangePageFunction={onChangePage}
      paginaAtual={paginaAtual || 1}
      quantidadeDePaginas={cobrancasPage?.totalPages || 0}
      quantidadeDeRegistros={cobrancasPage?.totalElements || 0}
      renderCheckBox={possuiPermissaoIniciarAcordo}
      onChangeCheckBox={(value: OpcoesSelecionaveisCheckBox) => changeCheckBox(value)}
      tipoCheckBox={"SELECT_OPTIONS"}
      isLoading={isLoading}
      checkedCheckBox={isLoading ? false : isCheckBoxChecked}
      disabledCheckBox={isVisualizacao}
    >
      <RenderRowsCobrancasVinculadasAoProcesso
        registros={cobrancasPage?.content || []}
        isVisualizacao={isVisualizacao}
        opcaoSelecionadaCheckBox={valueCheckBox}
        idsCobrancasSelecionadas={idsCobrancasSelecionadas}
        onSelecionarCobranca={(cobranca: RegistroContaReceberDTO) => onSelecionarCobranca(cobranca)}
        onDesmarcarCobranca={(cobranca: RegistroContaReceberDTO) => onDesmarcarCobranca(cobranca)}
      />
    </Tabela>
  );
};

export default TabelaCobrancasVinculadasAoProcesso;
